import React, { useEffect, useState } from "react";
import { createComponentStore, useStoreState } from "easy-peasy";
import {
  PageHeader,
  Button,
  Tooltip,
  Table,
  Input,
  message,
  Popconfirm,
} from "antd";
import UserStoreModel, { ACTION_TYPES } from "store/models/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm, Controller } from "react-hook-form";
import Yup from "ValidationModels/localizedYup";
import moment from "moment";
import FormLayout, { FormItem } from "Components/form";
import { useRouteMatch, Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers";
import { RULES } from "Constants/rules";
import { formatDate } from "Utils";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import FourOThree from "Pages/private/403";

const useUserStore = createComponentStore(UserStoreModel);

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputNode,
  record,
  form,
  index,
  children,
  ...restProps
}) => {
  if (!inputNode) inputNode = <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <FormItem required={true} error={form.errors.id}>
          <Controller
            defaultValue={record.name}
            control={form.control}
            name="name"
            as={<Input placeholder="Grup Adı" />}
          />
        </FormItem>
      ) : (
        children
      )}
    </td>
  );
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export default function UserGroupsPage({ ...props }) {
  const { abilities } = useStoreState((store) => store.auth);

  const [editingId, setEditingId] = useState("");
  const [store, actions] = useUserStore();
  const isEditing = (record) => record.id === editingId;
  const match = useRouteMatch();

  const isEditable = (record) => {
    return ![RULES.RULE0019, RULES.RULE0020].includes(record?.rule?.code);
  };

  const form = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        name: Yup.string().required(),
      })
    ),
  });

  const { handleSubmit, errors, control } = form;

  useEffect(() => {
    if (AuthCheck(abilities, AUTH_ACTIONS.READ, "Kullanıcı Grupları"))
      actions.getUserGroups();
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length > 0)
      message.error("Girdiğiniz değerleri kontrol ediniz.");
  }, [errors]);

  useEffect(() => {
    switch (store.process) {
      case ACTION_TYPES.USER_GROUP_CREATED:
        message.success("Kullanıcı grubu oluşturuldu.");
        actions.getUserGroups();
        setEditingId("");
        break;
      case ACTION_TYPES.USER_GROUP_UPDATED:
        message.success("Kullanıcı grubu güncellendi.");
        actions.getUserGroups();
        setEditingId("");
        break;
      case ACTION_TYPES.USER_GROUP_DELETED:
        message.success("Kullanıcı grubu kaldırıldı.");
        actions.getUserGroups();
        setEditingId("");
      default:
        break;
    }
  }, [store.process]);

  const columns = [
    {
      title: "Ad",
      dataIndex: "name",
      editable: true,
    },
    {
      title: "Yaratma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => {
        return formatDate(record.createdAt);
      },
    },
    {
      title: "İşlemler",
      width: 160,
      fixed: "right",
      dataIndex: "operation",
      render: (text, record) => {
        return isEditing(record) ? (
          <span>
            <Tooltip title="Sakla">
              <Button
                type="primary"
                htmlType="submit"
                icon={<FontAwesomeIcon icon={["far", "save"]} />}
                style={{ marginRight: 8 }}
              />
            </Tooltip>
            <Tooltip title="İptal">
              <Button
                onClick={() => {
                  if (record.id === "new") {
                    actions.setUserGroupsData(
                      store.userGroups.filter((item) => item.id !== record.id)
                    );
                  }

                  setEditingId("");
                }}
                icon={<FontAwesomeIcon icon={["fas", "undo-alt"]} />}
                style={{ marginRight: 8 }}
              />
            </Tooltip>
          </span>
        ) : (
          <>
            {isEditable(record) &&
              AuthCheck(
                abilities,
                AUTH_ACTIONS.WRITE,
                "Kullanıcı Grupları"
              ) && (
                <Tooltip title="Düzenle">
                  <Button
                    onClick={() => {
                      setEditingId(record.id);
                    }}
                    icon={<FontAwesomeIcon icon={["far", "edit"]} />}
                    style={{ marginRight: 8 }}
                  />
                </Tooltip>
              )}
            {AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Kullanıcı Grupları") && (
              <Tooltip title="Detaylar">
                <Link to={`${match.path}/${record.id}`}>
                  <Button
                    icon={<FontAwesomeIcon icon={["far", "eye"]} />}
                    style={{ marginRight: 8 }}
                  />
                </Link>
              </Tooltip>
            )}
            {isEditable(record) &&
              AuthCheck(
                abilities,
                AUTH_ACTIONS.DELETE,
                "Kullanıcı Grupları"
              ) && (
                <Popconfirm
                  title="Emin Misin?"
                  onConfirm={() => {
                    actions.deleteUserGroup({ id: record.id });
                    setEditingId("");
                  }}
                  okText="Sil"
                  cancelText="Geri"
                >
                  <Tooltip title="Sil">
                    <Button
                      disabled={editingId !== ""}
                      type="danger"
                      icon={<FontAwesomeIcon icon={["far", "trash-alt"]} />}
                      style={{ marginRight: 8 }}
                    />
                  </Tooltip>
                </Popconfirm>
              )}
          </>
        );
      },
    },
  ];

  const formOnSubmit = async (form) => {
    if (editingId === "new") {
      if (AuthCheck(abilities, AUTH_ACTIONS.NEW_RECORD, "Kullanıcı Grupları"))
        await actions.createUserGroup(form);
    } else {
      if (AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Kullanıcı Grupları"))
        await actions.updateUserGroup({ ...form, id: editingId });
    }
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputNode: col.inputNode,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        form: form,
      }),
    };
  });

  return (
    <>
      <PageHeader
        title="Kullanıcı Grupları"
        extra={[
          <Button
            key="new-user-group"
            onClick={() => {
              const newData = { id: "new", createdAt: moment() };
              actions.setUserGroupsData([newData, ...store.userGroups]);
              setEditingId(newData.id);
            }}
            disabled={
              !AuthCheck(
                abilities,
                AUTH_ACTIONS.NEW_RECORD,
                "Kullanıcı Grupları"
              )
            }
          >
            Yeni Kullanıcı Grubu
          </Button>,
        ]}
      />
      {AuthCheck(abilities, AUTH_ACTIONS.READ, "Kullanıcı Grupları") ? (
        <form
          onSubmit={handleSubmit(formOnSubmit)}
          style={{ padding: "0 24px" }}
        >
          <Table
            loading={store.process === ACTION_TYPES.FETCHING_USER_GROUPS}
            bordered
            rowKey={(row) => row.id}
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            dataSource={store.userGroups}
            columns={mergedColumns}
            size="small"
          />
        </form>
      ) : (
        <FourOThree />
      )}
    </>
  );
}
